import { useFxWorkspaceContext } from '@mobile-mapping/providers';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import {
  Component,
  JSX,
  Show,
  createEffect,
  createMemo,
  createSignal,
} from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';

import type { FxWorkspaceFolderDescendantRefTrnsGetter } from './fx-workspace-draft-checkbox-sources';
import {
  MissionTreeNodeData,
  MissionTreeNodeType,
} from './project-data-tree-nodes';
import { WorkspaceTreeItem } from './workspace-tree-nodes';

export type FxWorkspaceDraftCheckboxCategory =
  | 'missions'
  | 'models'
  | 'pointClouds'
  | 'trajectories'
  | 'mapWorkspaces';

function isMissionTreeNodeData(data: unknown): data is MissionTreeNodeData {
  if (typeof data !== 'object' || data === null || !('type' in data)) {
    return false;
  }

  const { type } = data;
  return (
    type === MissionTreeNodeType.Mission ||
    type === MissionTreeNodeType.Path ||
    type === MissionTreeNodeType.Runs ||
    type === MissionTreeNodeType.Run
  );
}

export function getProjectDataReferenceRefTrn<T>(
  category: FxWorkspaceDraftCheckboxCategory,
  node: TreeNodeDefinition<T>
): string | undefined {
  switch (category) {
    case 'missions': {
      if (!isMissionTreeNodeData(node.data)) {
        return undefined;
      }

      switch (node.data.type) {
        case MissionTreeNodeType.Path:
          return node.id;
        case MissionTreeNodeType.Mission:
        case MissionTreeNodeType.Run:
        case MissionTreeNodeType.Runs:
          return undefined;
        default:
          throw new UnreachableCaseError(node.data.type);
      }
    }
    case 'models': {
      const item = node.data as { type?: string } | undefined;
      if (node.isLeaf === false || item?.type === 'FOLDER') {
        return undefined;
      }

      return node.id;
    }
    case 'pointClouds':
    case 'trajectories':
      if (node.isLeaf === false || node.data === undefined) {
        return undefined;
      }

      return node.id;
    case 'mapWorkspaces': {
      const data = node.data as WorkspaceTreeItem | undefined;
      return data?.kind === 'layer' ? node.id : undefined;
    }
    default:
      throw new UnreachableCaseError(category);
  }
}

export function fxWorkspaceDraftTrailingContent<T>(
  category: FxWorkspaceDraftCheckboxCategory,
  node: TreeNodeDefinition<T>,
  isEditing: boolean,
  getFolderDescendantRefTrns: FxWorkspaceFolderDescendantRefTrnsGetter,
  getRemovableRefTrns?: FxWorkspaceFolderDescendantRefTrnsGetter
): JSX.Element | undefined {
  if (!isEditing) {
    return undefined;
  }

  if (category === 'missions' && isMissionTreeNodeData(node.data)) {
    const missionType = node.data.type;
    if (
      missionType === MissionTreeNodeType.Run ||
      missionType === MissionTreeNodeType.Runs
    ) {
      return undefined;
    }
  }

  const leafRefTrn = getProjectDataReferenceRefTrn(category, node);
  if (leafRefTrn !== undefined) {
    return <FxWorkspaceDraftLeafCheckbox refTrn={leafRefTrn} />;
  }

  const descendantRefTrns = getFolderDescendantRefTrns(node.id);
  const removableRefTrns = getRemovableRefTrns?.(node.id) ?? [];
  if (descendantRefTrns.length === 0 && removableRefTrns.length === 0) {
    return undefined;
  }

  return (
    <FxWorkspaceDraftFolderCheckbox
      nodeId={node.id}
      leafRefTrns={descendantRefTrns}
      removableRefTrns={removableRefTrns}
    />
  );
}

const stopTreeRowInteraction = (event: Event): void => {
  event.stopPropagation();
};

type ModusWcCheckboxHost = HTMLElement & {
  checked: boolean;
  value: boolean;
  indeterminate: boolean;
};

const syncModusWcCheckbox = (
  el: HTMLElement | undefined,
  checked: boolean,
  indeterminate = false
): void => {
  if (el === undefined) {
    return;
  }

  const host = el as ModusWcCheckboxHost;
  host.checked = checked;
  host.value = checked;
  host.indeterminate = indeterminate;

  // modus-wc-checkbox paints checked and indeterminate from the inner
  // input in shadowRoot. Light-DOM querySelector cannot reach that control.
  const input =
    el.shadowRoot?.querySelector('input[type="checkbox"]') ??
    el.querySelector('input[type="checkbox"]');
  if (input instanceof HTMLInputElement) {
    input.checked = checked;
    input.indeterminate = indeterminate;
  }
};

const FxWorkspaceDraftCheckboxRow: Component<{
  readonly children: JSX.Element;
}> = (props): JSX.Element => (
  <span
    class="inline-flex h-10 items-center pr-2"
    onClick={stopTreeRowInteraction}
    onPointerDown={stopTreeRowInteraction}
  >
    {props.children}
  </span>
);

const FxWorkspaceDraftLeafCheckbox: Component<{
  readonly refTrn: string;
}> = (props): JSX.Element => {
  const fxWorkspace = useFxWorkspaceContext();
  const isChecked = createMemo(() =>
    fxWorkspace.isDraftReference(props.refTrn)
  );
  const [checkboxEl, setCheckboxEl] = createSignal<HTMLElement>();

  createEffect(() => {
    syncModusWcCheckbox(checkboxEl(), isChecked());
  });

  return (
    <FxWorkspaceDraftCheckboxRow>
      <modus-wc-checkbox
        // @ts-expect-error Solid ref is valid but absent from modus-wc-checkbox JSX types
        ref={setCheckboxEl}
        size="lg"
        class="shrink-0"
        attr:data-testid={`workspace-draft-leaf-checkbox-${props.refTrn}`}
        value={isChecked()}
        on:inputChange={(event: Event) => {
          stopTreeRowInteraction(event);
          fxWorkspace.toggleDraftReference(props.refTrn);
        }}
      />
    </FxWorkspaceDraftCheckboxRow>
  );
};

const FxWorkspaceDraftFolderCheckbox: Component<{
  readonly nodeId: string;
  readonly leafRefTrns: readonly string[];
  readonly removableRefTrns?: readonly string[];
}> = (props): JSX.Element => {
  const fxWorkspace = useFxWorkspaceContext();
  const removableRefTrns = (): readonly string[] =>
    props.removableRefTrns ?? [];
  const selectedCount = createMemo(
    () =>
      props.leafRefTrns.filter((refTrn) => fxWorkspace.isDraftReference(refTrn))
        .length
  );
  const removableSelectedRefTrns = createMemo(() =>
    removableRefTrns().filter((refTrn) => fxWorkspace.isDraftReference(refTrn))
  );
  const isChecked = createMemo(
    () =>
      removableSelectedRefTrns().length === 0 &&
      props.leafRefTrns.length > 0 &&
      selectedCount() === props.leafRefTrns.length
  );
  const isIndeterminate = createMemo(
    () =>
      removableSelectedRefTrns().length > 0 ||
      (selectedCount() > 0 && selectedCount() < props.leafRefTrns.length)
  );
  const showCheckbox = createMemo(
    () => props.leafRefTrns.length > 0 || removableSelectedRefTrns().length > 0
  );
  const [checkboxEl, setCheckboxEl] = createSignal<HTMLElement>();

  createEffect(() => {
    syncModusWcCheckbox(checkboxEl(), isChecked(), isIndeterminate());
  });

  const handleToggle = (event: Event): void => {
    stopTreeRowInteraction(event);
    const storedRunRefTrns = removableSelectedRefTrns();
    // Stored run ids have no row checkbox. Clear them before the path toggle
    // so a later save does not keep the old per-run selection.
    if (storedRunRefTrns.length > 0) {
      fxWorkspace.setDraftReferenceSelection(storedRunRefTrns, false);
      return;
    }

    fxWorkspace.setDraftReferenceSelection(props.leafRefTrns, !isChecked());
  };

  return (
    <Show when={showCheckbox()}>
      <FxWorkspaceDraftCheckboxRow>
        <modus-wc-checkbox
          // @ts-expect-error Solid ref is valid but absent from modus-wc-checkbox JSX types
          ref={setCheckboxEl}
          size="lg"
          class="shrink-0"
          attr:data-testid={`workspace-draft-folder-checkbox-${props.nodeId}`}
          value={isChecked()}
          indeterminate={isIndeterminate()}
          on:inputChange={handleToggle}
        />
      </FxWorkspaceDraftCheckboxRow>
    </Show>
  );
};
